import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * LiveFab
 *
 * The floating "📡 Live broadcasts" action button the user sees in
 * the corner of the map.  Draggable so it never permanently covers
 * the wallet pill / disconnect button on mobile: each user can park
 * it wherever they want and the position persists across sessions
 * via localStorage.
 *
 * Implementation notes:
 *
 *  - We drive the drag off pointer events so touch, mouse and pen
 *    work identically.  A small movement tolerance distinguishes a
 *    deliberate tap from the start of a drag so a user can still
 *    tap the button to open the live directory.
 *
 *  - Position is stored as `{ right, bottom }` (viewport-anchored)
 *    so the FAB stays a consistent distance from the corner on
 *    window resize / rotate.  We clamp both to [16px, viewport-74px]
 *    to keep the FAB fully visible on every screen.
 *
 *  - The component takes no opinions on visibility — render it
 *    conditionally in App.tsx exactly as before.
 */

export interface LiveFabProps {
  onOpen: () => void;
  /** localStorage key.  Defaults to `geotrust:liveFabPos`. */
  storageKey?: string;
}

interface FabPos { right: number; bottom: number; }

const MOVE_TOLERANCE = 6;     // px — below this is a tap, above is a drag
const FAB_SIZE       = 58;    // must match the rendered size below
const EDGE_PADDING   = 12;    // min distance from the viewport edge
const DEFAULT_STORAGE_KEY = 'geotrust:liveFabPos';

/**
 * Fraction of the viewport width that Tesla's in-vehicle browser
 * reserves on the RIGHT for its own UI chrome / bezel, over which
 * web pointer events are either swallowed or never delivered.  The
 * user reported that the FAB only fires when parked near the
 * bottom-center; moving it past the centre-right makes it
 * unclickable.  Empirically the affected strip on a Model 3 is
 * roughly the right ~25% of the viewport, so we clamp the FAB's
 * `right` position to stay at least that far from the edge when the
 * UA looks like Tesla.  Phones and normal desktop browsers retain
 * the 12px edge padding from `EDGE_PADDING`.
 */
const TESLA_RIGHT_DEAD_ZONE_RATIO = 0.28;

/**
 * True when the UA looks like Tesla's in-vehicle browser (Model 3 /
 * Y post-Chromium upgrade, or older Model S/X `QtCarBrowser`).
 * Mirrors the detection used in ProfileOverlay so the warning
 * banner and the FAB clamping agree on "is this a Tesla".  UA
 * strings rotate between firmware releases, so a false negative
 * just means the user experiences the old behaviour — never a
 * regression for non-Tesla devices.
 */
function isTeslaBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Tesla|QtCarBrowser/i.test(ua);
}

/** Right-edge reserved zone in px for the current viewport. */
function rightEdgeReservedPx(): number {
  if (typeof window === 'undefined') return EDGE_PADDING;
  if (!isTeslaBrowser()) return EDGE_PADDING;
  return Math.max(EDGE_PADDING, Math.round(window.innerWidth * TESLA_RIGHT_DEAD_ZONE_RATIO));
}

/**
 * Viewport-aware default position.
 *
 * The previous default anchored the FAB to the bottom-right corner
 * (classic Material FAB convention), but on a Tesla Model 3 the
 * right-hand strip of the browser viewport is unresponsive to
 * pointer events (Tesla renders its own UI chrome there over the
 * web content in landscape).  A default in that zone leaves first-
 * time Tesla users unable to even drag the button away, trapping
 * them.
 *
 * Compromise: land the FAB slightly right of centre, lifted above
 * the mobile wallet pill.  That's reachable with a right-handed
 * thumb on phones AND inside the tappable zone on Tesla / kiosk
 * browsers.  Users can still drag it anywhere and their position
 * persists.
 */
function computeDefaultPos(): FabPos {
  if (typeof window === 'undefined') {
    return { right: 140, bottom: 92 };
  }
  const w = window.innerWidth;
  const h = window.innerHeight;
  const minRight = rightEdgeReservedPx();
  // ~35% of viewport width from the right edge, clamped so the FAB
  // never collides with the reserved right-edge zone at either
  // extreme.  On Tesla `minRight` is ~28% of viewport width, which
  // pushes this default further from the right edge than the 35%
  // baseline — so we Math.max both to guarantee the FAB lands in
  // the clickable zone on Tesla and roughly where it always landed
  // on everything else.
  const targetRight = Math.max(120, Math.round(w * 0.35));
  const maxRight = Math.max(minRight, w - FAB_SIZE - minRight);
  const right = Math.min(Math.max(minRight, targetRight), maxRight);
  // Lift above the mobile wallet pill (~70-90px tall when minimised)
  // so the FAB never covers the "restore wallet" button even for
  // first-time users who haven't dragged it yet.
  const bottom = Math.min(92, Math.max(EDGE_PADDING, h - FAB_SIZE - EDGE_PADDING));
  return { right, bottom };
}

function loadInitial(key: string): FabPos {
  if (typeof window === 'undefined') return computeDefaultPos();
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return computeDefaultPos();
    const parsed = JSON.parse(raw);
    if (typeof parsed?.right === 'number' && typeof parsed?.bottom === 'number') {
      return parsed;
    }
  } catch { /* ignore */ }
  return computeDefaultPos();
}

function clamp(pos: FabPos): FabPos {
  if (typeof window === 'undefined') return pos;
  const minRight  = rightEdgeReservedPx();
  const maxRight  = Math.max(minRight,     window.innerWidth  - FAB_SIZE - EDGE_PADDING);
  const maxBottom = Math.max(EDGE_PADDING, window.innerHeight - FAB_SIZE - EDGE_PADDING);
  return {
    // `minRight` is the viewport-edge reserved zone — on Tesla this
    // snaps any stored or in-progress drag position back out of the
    // unclickable right-side strip, so the user physically cannot
    // park the FAB where it won't respond to taps.
    right:  Math.min(Math.max(minRight, pos.right),      maxRight),
    bottom: Math.min(Math.max(EDGE_PADDING, pos.bottom), maxBottom),
  };
}

/**
 * Drop any saved FAB position so the next render falls back to the
 * viewport-aware default.  Exported so a "Reset Live button
 * position" menu entry can call it — useful when a user (usually
 * on Tesla / a kiosk) has dragged the FAB into a dead zone and
 * can't tap it to drag it back.
 *
 * Dispatches a `storage`-shaped custom event on `window` so any
 * mounted `<LiveFab>` can pick up the change without a page
 * reload.
 */
export function resetLiveFabPosition(storageKey: string = DEFAULT_STORAGE_KEY): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(storageKey); } catch { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent('geotrust:live-fab-reset', {
      detail: { storageKey },
    }));
  } catch { /* ignore */ }
}

export const LiveFab: React.FC<LiveFabProps> = ({ onOpen, storageKey = DEFAULT_STORAGE_KEY }) => {
  const [pos, setPos]       = useState<FabPos>(() => clamp(loadInitial(storageKey)));
  const [dragging, setDrag] = useState(false);

  // Listen for `resetLiveFabPosition()` so a menu click reverts the
  // FAB to the viewport-aware default without a reload.
  useEffect(() => {
    const onReset = (ev: Event) => {
      const detail = (ev as CustomEvent<{ storageKey?: string }>).detail;
      if (detail?.storageKey && detail.storageKey !== storageKey) return;
      setPos(clamp(computeDefaultPos()));
    };
    window.addEventListener('geotrust:live-fab-reset', onReset as EventListener);
    return () => window.removeEventListener('geotrust:live-fab-reset', onReset as EventListener);
  }, [storageKey]);

  // Kept in a ref so the pointermove/up handlers we register on
  // window always see the latest drag state without having to
  // re-register on every render.
  const dragRef = useRef<{
    startX: number; startY: number;
    startRight: number; startBottom: number;
    moved: boolean; pointerId: number;
  } | null>(null);

  // Persist position whenever it settles after a drag.
  useEffect(() => {
    try { window.localStorage.setItem(storageKey, JSON.stringify(pos)); } catch { /* ignore */ }
  }, [pos, storageKey]);

  // Re-clamp on window resize / rotate so the FAB never slides off
  // the screen when the user goes from landscape to portrait.
  useEffect(() => {
    const onResize = () => setPos(prev => clamp(prev));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handlePointerMove = useCallback((e: PointerEvent) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < MOVE_TOLERANCE) return;
    d.moved = true;
    setDrag(true);
    // right/bottom are anchored to the opposite edge, so the sign
    // of the delta inverts: drag right → smaller right value.
    setPos(clamp({
      right:  d.startRight  - dx,
      bottom: d.startBottom - dy,
    }));
  }, []);

  const handlePointerUp = useCallback((e: PointerEvent) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
    window.removeEventListener('pointercancel', handlePointerUp);
    const didDrag = d.moved;
    dragRef.current = null;
    setDrag(false);
    // A tap (no movement beyond tolerance) → fire onOpen.  We do
    // this in the up handler rather than a plain onClick so the
    // drag-vs-tap decision happens exactly once and there's no
    // race where a click fires after a tiny accidental drag.
    if (!didDrag) onOpen();
  }, [handlePointerMove, onOpen]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    // Only respond to the primary button on a mouse; touch/pen
    // have button === 0 by convention already.
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      startRight: pos.right, startBottom: pos.bottom,
      moved: false, pointerId: e.pointerId,
    };
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
  }, [pos.right, pos.bottom, handlePointerMove, handlePointerUp]);

  return (
    <button
      type="button"
      onPointerDown={handlePointerDown}
      aria-label="Live broadcasts"
      title={dragging ? 'Drag to reposition' : 'Live broadcasts (drag to move)'}
      style={{
        position: 'fixed',
        right: pos.right,
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${pos.bottom}px)`,
        width: FAB_SIZE, height: FAB_SIZE,
        borderRadius: '50%',
        border: 'none',
        background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
        color: '#fff',
        fontSize: 24,
        fontWeight: 700,
        boxShadow: dragging
          ? '0 16px 34px rgba(239,68,68,0.55), 0 4px 12px rgba(0,0,0,0.5)'
          : '0 10px 26px rgba(239,68,68,0.42), 0 2px 8px rgba(0,0,0,0.35)',
        cursor: dragging ? 'grabbing' : 'pointer',
        zIndex: 9000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none', // we own the gesture — stop Mobile Safari from hijacking
        transform: dragging ? 'scale(1.05)' : 'scale(1)',
        transition: dragging ? 'transform 80ms ease' : 'transform 120ms ease, box-shadow 120ms ease',
        userSelect: 'none',
      }}
    >
      <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {!dragging && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              inset: -4,
              borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.45)',
              animation: 'pulse 1.8s ease-in-out infinite',
            }}
          />
        )}
        <span style={{ fontSize: 22, pointerEvents: 'none' }}>📡</span>
      </span>
    </button>
  );
};

export default LiveFab;
